import React, { useEffect, useState } from 'react';
import './article.css';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { fr } from 'date-fns/locale';
import { format } from 'date-fns';
import { db } from '../../firebase';
import { FaArrowLeft, FaFacebookF, FaLink, FaXTwitter } from 'react-icons/fa6';
import { motion } from 'framer-motion';
import ArticleCard from "../../components/ArticleCard/ArticleCard";
import { Helmet } from 'react-helmet';
import useTrackArticleRead from '../../hooks/useTrackArticleRead ';
import Newsletter from "../../components/Newsletter/Newsletter";
import NewsletterPopup from '../../components/NewsletterPopup/NewsletterPopup';

const Article = () => {

    const navigate = useNavigate();
    const { id } = useParams();
    const [article, setArticle] = useState([]);
    const [categories, setCategories] = useState([]);
    const [articleContent, setArticleContent] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchLoading, setIsSearchLoading] = useState(false);

    useEffect(() => {
        const fetchArticle = async () => {
            const docRef = doc(db, 'articles', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setArticle(docSnap.data());
            }
        };

        const fetchCategories = async () => {
            const q = query(collection(db, 'categories'));
            onSnapshot(q, (snapshot) => {
                setCategories(snapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data()
                })));
            });
        };

        fetchArticle();
        fetchCategories();
    }, [id]);

    useTrackArticleRead(id, article.title);

    useEffect(() => {
        const fetchContent = async () => {
            if (article?.contentUrl) {
                try {
                    const response = await fetch(article.contentUrl);
                    const text = await response.text();
                    setArticleContent(text);
                } catch (error) {
                    console.error("Erreur lors du chargement du contenu :", error);
                }
            }
        };
        fetchContent();
    }, [article?.contentUrl]);

    const handleSearch = async (e) => {
        try {
            e.preventDefault();
            setIsSearchLoading(true);
            const searchRef = query(collection(db, 'articles'), where('titleLowerCase', '>=', searchTerm.toLowerCase()));
            onSnapshot(searchRef, (snapshot) => {
                setSearchResults(snapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data()
                })));
            });
        } catch (error) {
            console.error('Search error:', error);
        }
    };

    const endSearch = () => {
        setIsSearchLoading(false);
        setSearchTerm('');
    };

    const articleCover = { backgroundImage: `url(${article?.coverUrl})` };

    const articleUrl = `https://wearebiam.com/media/${id}`;

    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const fullText = `${article.title}\n${articleUrl}`;
        navigator.clipboard.writeText(fullText)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch((err) => {
                console.error("Erreur de copie :", err);
            });
    };

    const [author, setAuthor] = useState(null); // ou [] si tu préfères un tableau

    useEffect(() => {
        const fetchAuthor = async () => {
            try {
                const docRef = doc(db, "authors", article?.author);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setAuthor(docSnap.data()); // 👈 les données du document !
                } else {
                    console.log("Auteur introuvable.");
                }
            } catch (error) {
                console.error("Erreur lors du fetch de l'auteur :", error);
            }
        };

        if (article?.author) fetchAuthor();
    }, [article?.author]);

    if (!author) return (
        <div className="fixed inset-0 flex items-center justify-center bg-black z-[51]">
            <div className="w-16 h-16 border-8 border-white border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <>

            <Helmet>
                <title>{`WEAREBIAM | ${article.title}`}</title>
                <meta
                    name="description"
                    content={article.excerpt || 'Un article exclusif du média WEAREBIAM.'}
                />

                {/* Open Graph */}
                <meta property="og:title" content={`WEAREBIAM | ${article.title}`} />
                <meta
                    property="og:description"
                    content={article.excerpt || 'Un article exclusif du média WEAREBIAM.'}
                />
                <meta property="og:image" content={article.coverUrl} />
                <meta
                    property="og:url"
                    content={`https://wearebiam.com/media/${article.id}`}
                />
                <meta property="og:type" content="article" />
            </Helmet>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col items-center justify-center xl:gap-[100px] gap-[20px] lg:p-[5rem] lg:pb-[100px] pb-[80px] min-h-screen bg-black w-full"
            >
                <div className="flex flex-col xl:max-w-[1440px] w-full lg:gap-5 gap-[100px]">
                    <motion.section
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="flex flex-col gap-[30px] xl:min-w-[850px] w-full lg:mt-[20px] mt-[100px]"
                    >

                        <div className={`flex flex-col ${isSearchLoading ? 'gap-[30px]' : 'gap-[10px]'}`}>
                            {isSearchLoading && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.5 }}
                                    className="font-agrandir xl:text-[24px] text-[16px] text-tangerine"
                                >
                                    Résultats de la recherche
                                </motion.p>
                            )}

                            {isSearchLoading ? (
                                searchResults.length > 0 ? (
                                    searchResults.map((article, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <ArticleCard
                                                id={article.id}
                                                coverImg={article.data.coverUrl}
                                                title={article.data.title}
                                                content={article.data.content}
                                            />
                                        </motion.div>
                                    ))
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.4 }}
                                        className="flex justify-center w-full font-agrandir xl:text-[18px] text-[15px]"
                                    >
                                        <div className="flex flex-col">
                                            Nous n'avons rien trouvé, veuillez réessayer !
                                            <span className="h-1 w-14 bg-aqua" />
                                        </div>
                                    </motion.div>
                                )
                            ) : (
                                <div className="flex flex-col gap-10 w-full px-[1.25rem]">

                                    <button
                                        onClick={() => navigate(-1)}
                                        className="text-white text-[20px] hover:opacity-70 transition"
                                        title="Retour"
                                    >
                                        <FaArrowLeft />
                                    </button>

                                    <div className="flex lg:flex-row flex-col gap-10 ">
                                        <motion.div
                                            initial={{ scale: 0.95, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ duration: 0.6 }}
                                            className="lg:w-2/5 w-full bg-cover bg-center rounded-md lg:h-[65dvh] h-[55dvh] max-h-[600px] glow-animated"
                                            style={articleCover}
                                        />


                                        <motion.div
                                            initial={{ x: 50, opacity: 0 }}
                                            animate={{ x: 0, opacity: 1 }}
                                            transition={{ duration: 0.8 }}
                                            className="flex flex-col lg:w-3/5 gap-[30px]"
                                        >
                                            <div className="flex flex-col gap-5">

                                                <motion.div
                                                    className="flex gap-[30px] overflow-x-scroll"
                                                    initial="hidden"
                                                    animate="visible"
                                                    variants={{
                                                        hidden: { opacity: 0, y: 20 },
                                                        visible: {
                                                            opacity: 1,
                                                            y: 0,
                                                            transition: {
                                                                duration: 0.6,
                                                                ease: 'easeOut',
                                                                when: "beforeChildren",
                                                                staggerChildren: 0.2,
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {Array.isArray(article?.rubriques) && article.rubriques.length > 0 ? (
                                                        article.rubriques.map((rubrique, index) => (
                                                            <motion.p
                                                                key={index}
                                                                className="text-black bg-yellow ppmori font-bold rounded-md px-5 py-2.5"
                                                                initial={{ opacity: 0, y: 10 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                transition={{ duration: 0.3 }}
                                                                variants={{
                                                                    hidden: { opacity: 0, y: 20 },
                                                                    visible: { opacity: 1, y: 0 }

                                                                }}
                                                            >
                                                                {rubrique}
                                                            </motion.p>
                                                        ))
                                                    ) : (
                                                        <p className="text-white">Aucune rubrique disponible</p>
                                                    )}
                                                </motion.div>

                                                <motion.p
                                                    className="ppmori xl:text-[36px] text-[20px] text-white font-bold"
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.2 }}
                                                >
                                                    {article?.title}
                                                </motion.p>

                                                <motion.div
                                                    className="flex lg:flex-row flex-col w-full lg:items-center gap-5 justify-between"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 0.3 }}
                                                >
                                                    <div className="flex gap-2 items-center text-grey">
                                                        <div className="h-9 w-9 rounded-full bg-cover bg-center"
                                                            style={{ backgroundImage: `url(${author.profilPicture})` }} />
                                                        <p className="flex gap-2">
                                                            <span>by</span> {author.displayName}
                                                        </p>
                                                        -
                                                        <p>{article?.createdAt && format(article.createdAt.toDate(), 'dd MMMM yyyy', { locale: fr })}</p>
                                                    </div>

                                                    <div className="flex gap-2.5 items-center">
                                                        {/* Facebook */}
                                                        <a
                                                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center justify-center text-white h-10 w-10 bg-[#1F1F1F] rounded-full"
                                                        >
                                                            <FaFacebookF />
                                                        </a>

                                                        {/* Twitter */}
                                                        <a
                                                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(articleUrl)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center justify-center text-white h-10 w-10 bg-[#1F1F1F] rounded-full"
                                                        >
                                                            <FaXTwitter />
                                                        </a>

                                                        <button
                                                            onClick={handleCopy}
                                                            className="flex items-center justify-center text-white h-10 w-10 bg-[#1F1F1F] rounded-full relative"
                                                            title="Copier le lien"
                                                        >
                                                            <FaLink />
                                                            {copied && (
                                                                <span className="absolute -top-8 flex items-center justify-center w-[80px] text-[12px] bg-[#1F1F1F] text-white px-2 py-1 rounded-full">
                                                                    Lien copié !
                                                                </span>
                                                            )}
                                                        </button>
                                                    </div>

                                                </motion.div>

                                                <motion.p
                                                    className="ppmori xl:text-[20px] text-[20px] text-white text-justify article-text-content"
                                                    dangerouslySetInnerHTML={{ __html: article?.content }}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 0.4 }}
                                                />
                                            </div>
                                        </motion.div>
                                    </div>
                                </div>

                            )}
                        </div>
                    </motion.section>

                    <NewsletterPopup />

                    <Newsletter />

                </div>
            </motion.div>
        </>
    );
};

export default Article;
